import {
  ListingResource,
  useListingField,
  useListingMetadata,
  useListingStore,
} from '@brightmls/nextgen.lae.flc';
import { useModal } from '../../../../contexts/ModalContext';
import { useSidePanel } from '../../../../contexts/SidePanelContext';
import { PropertyField } from '../../../../system/field';
import { useService } from '../../../../contexts/ServiceContext';
import {
  ListingContactsRequest,
  ListingContactsResponse,
  ContactInfo,
} from './types';
import { ShareDraftPanel } from './components/ShareDraftPanel';
import { ShareDraftLoadingPanel } from './components/ShareDraftLoadingPanel';

// Only the fields needed for the backend API
export const ListingContactFieldList = [
  PropertyField.LISTING_ID,
  PropertyField.LISTING_KEY,
  PropertyField.LIST_AGENT_KEY,
  PropertyField.CO_LIST_AGENT_KEY,
  PropertyField.BUYER_AGENT_KEY,
  PropertyField.CO_BUYER_AGENT_KEY,
  PropertyField.LIST_OFFICE_KEY,
] as const;

// Address fields for frontend display only
export const AddressFieldList = [
  PropertyField.LISTING_ID,
  PropertyField.LISTING_KEY,
  PropertyField.STREET_NUMBER,
  PropertyField.STREET_DIR_PREFIX,
  PropertyField.STREET_NAME,
  PropertyField.STREET_SUFFIX,
  PropertyField.STREET_SUFFIX_MODIFIER,
  PropertyField.STREET_DIR_SUFFIX,
  PropertyField.UNIT_NUMBER,
  PropertyField.CITY,
  PropertyField.STATE_OR_PROVINCE,
  PropertyField.POSTAL_CODE,
] as const;

export const useShareDraft = () => {
  const { isFromLiveListing } = useListingMetadata();
  const { showModal } = useModal();
  const { showPanel, hidePanel } = useSidePanel();
  const { getListingContacts } = useService();

  const isOfficeExclusive = useListingField<boolean>(
    ListingResource.Property,
    PropertyField.OFFICE_EXCLUSIVE_YN,
    false,
  );

  // Get owner fields
  const owner1Name = useListingField<string>(
    ListingResource.Property,
    PropertyField.OWNER_1_NAME,
    '',
  );
  const owner1Email = useListingField<string>(
    ListingResource.Property,
    PropertyField.OWNER_1_EMAIL,
    '',
  );
  const owner1Phone = useListingField<string>(
    ListingResource.Property,
    PropertyField.OWNER_1_MOBILE_PHONE,
    '',
  );
  const owner2Name = useListingField<string>(
    ListingResource.Property,
    PropertyField.OWNER_2_NAME,
    '',
  );
  const owner2Email = useListingField<string>(
    ListingResource.Property,
    PropertyField.OWNER_2_EMAIL,
    '',
  );
  const owner2Phone = useListingField<string>(
    ListingResource.Property,
    PropertyField.OWNER_2_MOBILE_PHONE,
    '',
  );

  const handleContactsResponse = (
    response: ListingContactsResponse,
    addressData: any,
  ) => {
    if (!response.success) {
      hidePanel(); // Hide loading panel
      showModal({
        content: `Failed to retrieve contacts: ${response.error || 'Unknown error'}`,
        cancelText: 'OK',
      });
      return;
    }

    // Create owner contacts from listing fields
    const ownerContacts: ContactInfo[] = [];

    if (owner1Email.value && owner1Name.value) {
      ownerContacts.push({
        key: 'owner1',
        type: 'Owner #1',
        name: owner1Name.value,
        email: owner1Email.value,
        phone: owner1Phone.value || null,
        mlsId: null,
        isOwner: true,
        requiresOtp: true,
        ownerNumber: 'Owner1',
      } as ContactInfo);
    }

    if (owner2Email.value && owner2Name.value) {
      ownerContacts.push({
        key: 'owner2',
        type: 'Owner #2',
        name: owner2Name.value,
        email: owner2Email.value,
        phone: owner2Phone.value || null,
        mlsId: null,
        isOwner: true,
        requiresOtp: true,
        ownerNumber: 'Owner2',
      } as ContactInfo);
    }

    // Merge owner contacts with backend contacts
    const mergedContacts = {
      ...response.contacts,
      other: [...(response.contacts?.other || []), ...ownerContacts],
      all: [...(response.contacts?.all || []), ...ownerContacts],
    };

    // Replace loading panel with actual share draft panel
    showPanel(
      'Share Draft',
      <ShareDraftPanel
        listingData={{
          listingKey: addressData.LISTING_KEY || '',
          mlsId: addressData.LISTING_ID || '',
          address: {
            streetNumber: addressData.STREET_NUMBER,
            streetDirPrefix: addressData.STREET_DIR_PREFIX,
            streetName: addressData.STREET_NAME,
            streetSuffix: addressData.STREET_SUFFIX,
            streetSuffixModifier: addressData.STREET_SUFFIX_MODIFIER,
            streetDirSuffix: addressData.STREET_DIR_SUFFIX,
            unitNumber: addressData.UNIT_NUMBER,
            city: addressData.CITY,
            state: addressData.STATE_OR_PROVINCE,
            zipCode: addressData.POSTAL_CODE,
          },
        }}
        contacts={mergedContacts as any}
        onClose={hidePanel}
      />,
      undefined, // No footer needed
      600, // Wider panel for better layout
      hidePanel, // Close callback
    );
  };

  const onShareDraft = async (isCompleted: boolean) => {
    // Validation: Check if listing is complete
    if (!isCompleted) {
      showModal({
        content:
          'This listing has missing required fields and is not available to be shared. Please return to the form and provide information to the missing required fields and try again.',
        cancelText: 'Edit Listing',
      });
      return;
    }

    // Validation: Check if listing is already published (and not office exclusive)
    if (isFromLiveListing && !isOfficeExclusive.value) {
      showModal({
        content:
          'This listing has already been published and is not available to be shared.',
        cancelText: 'OK',
      });
      return;
    }

    const state = useListingStore.getState();

    // Build the request with only contact keys for the backend
    const request: ListingContactsRequest = ListingContactFieldList.reduce(
      (payload, field) => {
        const value = state[ListingResource.Property][field];
        // Only include non-empty values
        if (value !== null && value !== undefined && value !== '') {
          payload[field] = value;
        }
        return payload;
      },
      {} as Record<string, any> as ListingContactsRequest,
    );

    // Get address data for frontend display (not sent to backend)
    const addressData = AddressFieldList.reduce(
      (data, field) => {
        data[field] = state[ListingResource.Property][field];
        return data;
      },
      {} as Record<string, any>,
    );

    try {
      // Show loading panel first
      showPanel(
        'Share Draft',
        <ShareDraftLoadingPanel />,
        undefined,
        600,
        hidePanel,
      );

      // Fetch contacts from backend (only sending contact keys)
      const response = await getListingContacts(request);

      // Handle the response with address data for display
      handleContactsResponse(response, addressData);
    } catch (error) {
      console.error('Error fetching contacts:', error);

      showModal({
        content: 'Failed to retrieve listing contacts. Please try again.',
        cancelText: 'OK',
      });
    }
  };

  return {
    onShareDraft,
  };
};
